import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * There are two contexts:
 * ReadOnlyThemeContext:
 *  this context supplies the theme and color scheme data from web-shell to the web app,
 *  but does not allow theme or color scheme to be changed
 * ThemeContext:
 *  this context exposes set functions for theme and color scheme and is used within
 *  plugins to change the plugin theme/scheme
 */

// eslint-disable-next-line import/no-unresolved
import * as React from 'react';

/**
 * Previous props when using as ThemeContext.Provider directly
 *
 * @deprecated apply these values directly as props instead
 */

/**
 * @deprecated
 */
export var dsThemeContextManager = {
  /** @param value - new value for the baseTheme */
  setTheme: function setTheme(value) {
    dsThemeContextManager.theme = value;
  },
  /** Method to  get the current baseTheme in use. */
  getCurrentTheme: function getCurrentTheme() {
    return dsThemeContextManager.theme;
  }
};

/**
 * Utility to allow apps to set starting baseTheme.
 *
 * @deprecated
 */
export var setTheme = function setTheme(value) {
  dsThemeContextManager.setTheme(value);
};

/**
 * ReadOnlyThemeContext is consumed by the web-shell to wrap web applications
 * It provides access to the theme and color scheme defined in the app config
 * and is read-only to disallow plugins from rewriting the app theme or color scheme
 */
export var ReadOnlyThemeContext = /*#__PURE__*/React.createContext({
  theme: 'intuit',
  colorScheme: 'light'
});

/**
 * ReadOnlyThemeProvider is a wrapper for ReadOnlyThemeContext.
 * Its only consumer should be the web-shell.
 */
export var ReadOnlyThemeProvider = function ReadOnlyThemeProvider(_ref) {
  var children = _ref.children,
    suppliedTheme = _ref.theme,
    suppliedColorScheme = _ref.colorScheme,
    suppliedValues = _ref.value;
  var theme = suppliedTheme || (suppliedValues === null || suppliedValues === void 0 ? void 0 : suppliedValues.theme);
  var colorScheme = suppliedColorScheme || (suppliedValues === null || suppliedValues === void 0 ? void 0 : suppliedValues.colorScheme);
  return /*#__PURE__*/React.createElement(ReadOnlyThemeContext.Provider, {
    value: {
      theme: theme,
      colorScheme: colorScheme
    }
  }, /*#__PURE__*/React.createElement("div", {
    "data-theme": theme,
    "data-colorScheme": colorScheme
  }, children));
};

/**
 * ThemeContext is intended for plugins to use in order to override their plugin's
 * theme and color scheme. If a plugin does not explicitly wrap their widget with
 * a ThemeProvider, they will be consuming the ReadOnlyThemeContext from web-shell.
 */
export var ThemeContext = /*#__PURE__*/React.createContext({
  theme: undefined,
  colorScheme: undefined,
  setTheme: function setTheme() {
    return null;
  },
  setColorScheme: function setColorScheme() {
    return null;
  }
});

/**
 * The default setter function for the ThemeContext
 * This function is used when the ThemeProvider is not provided with a setTheme or setColorScheme function
 *
 * @param setterType - the type of setter function being called
 * @returns a console error message
 */
var defaultSetter = function defaultSetter(setterType) {
  return function () {
    // eslint-disable-next-line no-console
    console.error("To access the ".concat(setterType, " function within the ThemeProvider, you must provide the ").concat(setterType, " function as a prop to the ThemeProvider"));
  };
};

/**
 * Component that wraps ThemeProvider children and supplied data-theme and data-colorScheme attributes
 *
 * This component is a slightly modified copypasta from:
 * https://stackoverflow.com/questions/65123286/inject-custom-data-attribute-to-react-props-children
 */
var AttributeWrapper = function AttributeWrapper(_ref2) {
  var _ref2$theme = _ref2.theme,
    theme = _ref2$theme === void 0 ? 'intuit' : _ref2$theme,
    _ref2$colorScheme = _ref2.colorScheme,
    colorScheme = _ref2$colorScheme === void 0 ? 'light' : _ref2$colorScheme,
    children = _ref2.children;
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    parentElement = _React$useState2[0],
    setParentElement = _React$useState2[1];
  var Wrapper = parentElement ? React.Fragment : 'div';
  var ref = React.useRef(null);
  var targetIndex = React.useRef(0);

  /**
   * This effect is used to set the parentElement and targetIndex
   */
  React.useEffect(function () {
    var _ref$current;
    if ((_ref$current = ref.current) !== null && _ref$current !== void 0 && _ref$current.parentElement) {
      targetIndex.current = Array.from(ref.current.parentElement.children).indexOf(ref.current);
      setParentElement(ref.current.parentElement);
    }
  }, []);

  /**
   * Once the parentElement and targetIndex are set,
   * this effects iterates over the parentElement's children
   * to set the data-theme and data-colorScheme attributes on each child
   */
  React.useEffect(function () {
    if (parentElement && parentElement.firstChild instanceof HTMLElement) {
      Array.from(parentElement.children).forEach(function (child) {
        // eslint-disable-next-line no-param-reassign
        child.dataset.theme = theme;
        // eslint-disable-next-line no-param-reassign
        child.dataset.colorscheme = colorScheme;
      });
    }
  }, [parentElement, targetIndex, theme, colorScheme]);

  /**
   * Once the parentElement exists, we no longer need the wrapper div
   * So this component removes the wrapper div and replaces it with the children
   */
  return /*#__PURE__*/React.createElement(Wrapper, {
    ref: parentElement ? undefined : ref
  }, children);
};

/**
 * ThemeProvider is a wrapper for ThemeContext and the recommended way of providing
 * the ThemeContext to plugins.
 */
export var ThemeProvider = function ThemeProvider(_ref3) {
  var children = _ref3.children,
    suppliedTheme = _ref3.theme,
    suppliedColorScheme = _ref3.colorScheme,
    suppliedValues = _ref3.value,
    _ref3$setTheme = _ref3.setTheme,
    setNewTheme = _ref3$setTheme === void 0 ? defaultSetter('setTheme') : _ref3$setTheme,
    _ref3$setColorScheme = _ref3.setColorScheme,
    setColorScheme = _ref3$setColorScheme === void 0 ? defaultSetter('setColorScheme') : _ref3$setColorScheme;
  var theme = suppliedTheme || (suppliedValues === null || suppliedValues === void 0 ? void 0 : suppliedValues.theme);
  var colorScheme = suppliedColorScheme || (suppliedValues === null || suppliedValues === void 0 ? void 0 : suppliedValues.colorScheme);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: {
      theme: theme,
      colorScheme: colorScheme,
      setTheme: setNewTheme,
      setColorScheme: setColorScheme
    }
  }, /*#__PURE__*/React.createElement(AttributeWrapper, {
    theme: theme,
    colorScheme: colorScheme
  }, children));
};
//# sourceMappingURL=context.js.map